// Globals
import { memo, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Button } from '@/ui';
import { UploadProps } from 'antd';
import { StyledBlock, StyledButtons, StyledFileName, StyledFileUpload, StyledInfo } from './FileUploadStyles';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';

// Helpers
import { imageFileDownload } from '@/utils';

type IFileUploadProps = Omit<UploadProps, 'onChange'> & {
  value?: string | File;
  onChange?: (file?: File | null, imageUrl?: string) => void;
  accept?: string;
  recommendedFormats?: string;
  fileName?: string;
};

const FileUpload = ({ onChange, fileName, value, accept, recommendedFormats, ...props }: IFileUploadProps) => {
  const { t } = useTranslation(['common']);
  const [initialFile, setInitialFile] = useState<string | null>(null);

  useEffect(() => {
    if (!value) return;

    const file = typeof value === 'string' ? value : URL.createObjectURL(value);
    setInitialFile(file);
    return () => {
      setInitialFile(null);
      if (typeof value === 'string') return;
      URL.revokeObjectURL(file);
    };
  }, [value]);

  const handleSelectFile = useCallback(
    async (file: File) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        const url = reader.result as string;
        onChange?.(file, url);
      };
      return false;
    },
    [onChange],
  );

  const handleDownloadFile = async (e: SyntheticEvent) => {
    e.stopPropagation();
    if (typeof value !== 'string') return;
    try {
      const fileBlob = await fetch(value).then((res) => res.blob());

      imageFileDownload(fileBlob, fileName ?? 'file');
    } catch {}
  };

  const handleDelete = async (e: SyntheticEvent) => {
    e.stopPropagation();
    setInitialFile(null);
    onChange?.(null);
  };

  return (
    <StyledBlock>
      {recommendedFormats && (
        <StyledInfo>
          {t('recommended_formats', {
            formats: recommendedFormats,
          })}
        </StyledInfo>
      )}
      <StyledFileUpload beforeUpload={handleSelectFile} showUploadList={false} accept={accept} {...props}>
        {!!initialFile && (
          <StyledFileName>{typeof value === 'string' ? fileName : (value?.name ?? fileName)}</StyledFileName>
        )}
        <StyledButtons>
          {typeof value === 'string' && <Button onClick={handleDownloadFile} icon={<DownloadOutlined />} />}
          {!!initialFile && <Button onClick={handleDelete} icon={<DeleteOutlined />} />}
          <Button>{t('btn_upload_new')}</Button>
        </StyledButtons>
      </StyledFileUpload>
    </StyledBlock>
  );
};
export default memo(FileUpload);
